import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  FileText, Search, RefreshCw, Loader2, Settings2,
  Plane, MapPin, Radio, Wind, AlertTriangle, Navigation,
  Clock, Info, ExternalLink, Building, Users,
  Shield, Compass, Eye, Layers, BarChart3, Volume2,
  CloudSnow, Route, ChevronRight, Plus, Bookmark
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Default Quick Reference sections that can be customized by Master Admin
const DEFAULT_QUICK_REF_SECTIONS = ["QR-GA-STANDS", "QR-TRAFFIC", "QR-NOISE"];

// Quick Reference Content Cards
const QUICK_REF_CARDS = {
  "QR-GA-STANDS": {
    id: "QR-GA-STANDS",
    title: "GA Stand Restrictions (P1-P5)",
    icon: Plane,
    color: "emerald",
    content: `SOUTH APRON STAND RESTRICTIONS (General Aviation)

P1 (PCN 30):
• Code A/B aircraft only
• Max: Height 4.78M, Length 18M, Wingspan 16.61M

P2 (PCN 58):
• Code A/B aircraft only
• Max: Height 4.78M, Length 18M, Wingspan 16.61M

P3 (PCN 50):
• Wingspan and length 20M MAX only

P4 (PCN 72):
• Stands 19,20,21,23,24,26,27: Wingspan 13.60M MAX
• Stands 22,25,28: Wingspan 12M MAX
• No long duration parking of tail wheel aircraft on 26,27,28

P5 (PCN 72):
• Medium body aircraft MAX
• Tail wheel aircraft PROHIBITED
• Stand 30: Wingspan >21M / Length >22M not available
• Stands 31,32,33,34: Wingspan >32M not available

ENGINE RUNNING LIMIT: 5 MIN MAX on P1-P4`
  },
  "QR-TRAFFIC": {
    id: "QR-TRAFFIC",
    title: "Traffic Regulations & Training",
    icon: AlertTriangle,
    color: "orange",
    content: `SPECIFIC TRAFFIC REGULATIONS

PROHIBITED ACTIVITIES:
• Aircraft without radio
• Glider flights
• ULM flights
• Balloon flights
• Parachuting overhead aerodrome
• Acrobatic flights within traffic circuit

TRAINING AND TEST FLIGHTS:
• Only by based aircraft/operators
• Wingspan < 36M required
• Propeller aircraft ≤6T MTOW: 0600-2100 (0500-2000)
• Prior permission from Charleroi TWR required
• During LVP: ALL training flights PROHIBITED

VFR RESTRICTIONS:
• Non-based VFR flights PROHIBITED
  unless able to maintain 160 KT on approach
  (except helicopters)

MILITARY:
• TWY T2 and T3 restricted to military use`
  },
  "QR-NOISE": {
    id: "QR-NOISE",
    title: "Noise Abatement Procedures",
    icon: Volume2,
    color: "red",
    content: `NOISE ABATEMENT SUMMARY

OPERATING HOURS:
• AD Operator: 0530-2200 (0430-2100)
• At 2200 (2100) AERODROME CLOSED

RESTRICTIONS 0530-0600 AND 2100-2200:
• MTOW >8618 KG: Max QC of 5 per movement

ENGINE TEST RUNS:
• High power: PROHIBITED on stands
• Outdoor high power: PROHIBITED 2000-0800
• Idle checks: Only during opening hours
• Full power: On P5 only, when RWY 24 in use

DEPARTURE NOISE ABATEMENT:
• Climb gradient: 4% MIN until 3000 FT QNH
• Take-off to 2100 FT: V2+10 KT MIN
• At 2100 FT: Reduce power per schedule
• At 3600 FT: Accelerate to en-route climb

ARRIVAL:
• Use continuous descent when conditions permit
• CDO facilitated at ATC discretion`
  },
  "QR-SOUTH": {
    id: "QR-SOUTH",
    title: "North Apron (P10-P15)",
    icon: Building,
    color: "amber",
    content: `COMMERCIAL APRON - NORTH SIDE (P10-P15)

PCN: 73/R/B/W/T (all aprons)

LARGE AIRCRAFT STANDS:
Stands 51B, 55B, 57B, 67B, 80B for:
• A340-300, A330-200, A330-900
• Code D aircraft

P15: No Code D aircraft

APRON MOVEMENT (RWY 24 in use):
• Inbound: via TWY M4, M5 or M6
• Outbound: via TWY M5, M6 or M7
• Push facing east, clockwise movement

APRON MOVEMENT (RWY 06 in use):
• Inbound: via TWY M5, M6 or M7
• Outbound: via TWY M4, M5 or M6
• Push facing west, counter-clockwise

PUSHBACK:
• Wingspan <36M: taxilane F
• A340/A330: taxilane G
• 180° turns on Apron South: FORBIDDEN`
  },
  "QR-LVP": {
    id: "QR-LVP",
    title: "Low Visibility Procedures",
    icon: Eye,
    color: "purple",
    content: `LOW VISIBILITY PROCEDURES

CAT II/III FACILITIES (RWY 24):
• CAT II: RVR 550M to 300M
• CAT III: RVR 300M to 150M
• Take-off: RVR 150M MIN

LVP CRITERIA:
• Preparation: Vis <1500M and/or ceiling ≤300 FT
• Operations: RVR <550M and/or ceiling <200 FT
• Termination: Vis ≥800M and ceiling ≥200 FT

RESTRICTIONS DURING LVP:
• AVGAS station CLOSED
• ALL training flights PROHIBITED
• Vehicle movements restricted
• Follow-me mandatory for certain ops`
  },
  "QR-TAXI": {
    id: "QR-TAXI",
    title: "Taxi Regulations",
    icon: Navigation,
    color: "gold",
    content: `TAXIWAY RESTRICTIONS

GENERAL:
• TWY S2/S3: Prohibited wingspan >36M
• TWY S4: Prohibited wingspan >20M (right turn to THR 24)
• Max 2 aircraft wingspan ≥24M on RWY+TWY S

WIDE BODY OPERATIONS:
• Wingspan ≥36M on RWY: No aircraft on TWY S
• A340-300 on TWY N: Outer engines idle (FOD)
• A340/A330 on TWY S: Only via S1/S4 with approval,
  follow-me car and wingman required

RWY 24 BACKTRACK:
• 180° only on turn pad after RWY end
• Aircraft >20T only

MILITARY:
• TWY T2 and T3: Military use only`
  }
};

// Complete AIP sections with subsections as requested
const AIP_SECTIONS = [
  // EBCI AD 2.2
  { id: "AD 2.2", title: "Aerodrome Geographical and Administrative Data", icon: MapPin, category: "general", color: "blue", subsections: [] },
  
  // EBCI AD 2.3
  { id: "AD 2.3", title: "Operational Hours", icon: Clock, category: "general", color: "blue", subsections: [] },
  
  // EBCI AD 2.4
  { id: "AD 2.4", title: "Handling Services and Facilities", icon: Settings2, category: "general", color: "blue", subsections: [] },
  
  // EBCI AD 2.5
  { id: "AD 2.5", title: "Passenger Facilities", icon: Users, category: "general", color: "blue", subsections: [] },
  
  // EBCI AD 2.6
  { id: "AD 2.6", title: "Rescue and Fire Fighting Services", icon: Shield, category: "safety", color: "red", subsections: [] },
  
  // EBCI AD 2.7
  { id: "AD 2.7", title: "Runway Surface Condition Assessment and Reporting, and Snow Plan", icon: CloudSnow, category: "general", color: "blue", subsections: [] },
  
  // EBCI AD 2.8 - with apron subsections
  { id: "AD 2.8", title: "Aprons, Taxiways and Check Locations/Positions Data", icon: Navigation, category: "apron", color: "gold", subsections: [] },
  { id: "AD 2.8-N", title: "North Apron (P10-P15) - Commercial", icon: Building, category: "north", color: "amber", parent: "AD 2.8" },
  { id: "AD 2.8-S", title: "South Apron (P1-P5) - General Aviation", icon: Plane, category: "ga", color: "emerald", parent: "AD 2.8" },
  
  // EBCI AD 2.9
  { id: "AD 2.9", title: "Surface Movement Guidance and Control System and Markings", icon: Compass, category: "general", color: "blue", subsections: [] },
  
  // EBCI AD 2.10
  { id: "AD 2.10", title: "Aerodrome Obstacles", icon: AlertTriangle, category: "obstacles", color: "red", subsections: [] },
  
  // EBCI AD 2.11
  { id: "AD 2.11", title: "Meteorological Information Provided", icon: Wind, category: "general", color: "blue", subsections: [] },
  
  // EBCI AD 2.12
  { id: "AD 2.12", title: "Runway Physical Characteristics", icon: Plane, category: "runway", color: "purple", subsections: [] },
  
  // EBCI AD 2.13
  { id: "AD 2.13", title: "Declared Distances", icon: BarChart3, category: "runway", color: "purple", subsections: [] },
  
  // EBCI AD 2.14
  { id: "AD 2.14", title: "Approach and Runway Lighting", icon: Eye, category: "runway", color: "purple", subsections: [] },
  
  // EBCI AD 2.15
  { id: "AD 2.15", title: "Other Lighting and Secondary Power Supply", icon: Eye, category: "runway", color: "purple", subsections: [] },
  
  // EBCI AD 2.16
  { id: "AD 2.16", title: "Helicopter Landing Area", icon: Plane, category: "runway", color: "purple", subsections: [] },
  
  // EBCI AD 2.17
  { id: "AD 2.17", title: "ATS Airspace", icon: Layers, category: "ats", color: "teal", subsections: [] },
  
  // EBCI AD 2.18
  { id: "AD 2.18", title: "ATS Communication Facilities", icon: Radio, category: "ats", color: "teal", subsections: [] },
  
  // EBCI AD 2.19
  { id: "AD 2.19", title: "Radio Navigation and Landing Aids", icon: Radio, category: "ats", color: "teal", subsections: [] },
  
  // EBCI AD 2.20 - with subsections
  { id: "AD 2.20", title: "Local Aerodrome Regulations", icon: FileText, category: "regulations", color: "orange", 
    subsections: ["General", "Taxi Regulations", "Apron Regulations", "Runway Regulations", "Specific Traffic Regulations"] },
  { id: "AD 2.20-GEN", title: "General", icon: FileText, category: "regulations", color: "orange", parent: "AD 2.20" },
  { id: "AD 2.20-TAXI", title: "Taxi Regulations", icon: FileText, category: "regulations", color: "orange", parent: "AD 2.20" },
  { id: "AD 2.20-APRON", title: "Apron Regulations", icon: FileText, category: "regulations", color: "orange", parent: "AD 2.20" },
  { id: "AD 2.20-RWY", title: "Runway Regulations", icon: FileText, category: "regulations", color: "orange", parent: "AD 2.20" },
  { id: "AD 2.20-SPEC", title: "Specific Traffic Regulations", icon: FileText, category: "regulations", color: "orange", parent: "AD 2.20" },
  
  // EBCI AD 2.21 - with subsections  
  { id: "AD 2.21", title: "Noise Abatement Procedures", icon: Volume2, category: "regulations", color: "orange",
    subsections: ["General", "Ground Procedures", "Arrival Procedures", "Departure Procedures"] },
  { id: "AD 2.21-GEN", title: "General", icon: Volume2, category: "regulations", color: "orange", parent: "AD 2.21" },
  { id: "AD 2.21-GND", title: "Ground Procedures", icon: Volume2, category: "regulations", color: "orange", parent: "AD 2.21" },
  { id: "AD 2.21-ARR", title: "Arrival Procedures", icon: Volume2, category: "regulations", color: "orange", parent: "AD 2.21" },
  { id: "AD 2.21-DEP", title: "Departure Procedures", icon: Volume2, category: "regulations", color: "orange", parent: "AD 2.21" },
  
  // EBCI AD 2.22 - with subsections
  { id: "AD 2.22", title: "Flight Procedures", icon: Route, category: "procedures", color: "cyan",
    subsections: ["IFR Flights (Inbound)", "IFR Flights (Outbound)", "Low Visibility Procedures", "VFR Flights", "Radio Communication Failure"] },
  { id: "AD 2.22-IFR-IN", title: "IFR Flights (Inbound)", icon: Route, category: "procedures", color: "cyan", parent: "AD 2.22" },
  { id: "AD 2.22-IFR-OUT", title: "IFR Flights (Outbound)", icon: Route, category: "procedures", color: "cyan", parent: "AD 2.22" },
  { id: "AD 2.22-LVP", title: "Low Visibility Procedures", icon: Route, category: "procedures", color: "cyan", parent: "AD 2.22" },
  { id: "AD 2.22-VFR", title: "VFR Flights", icon: Route, category: "procedures", color: "cyan", parent: "AD 2.22" },
  { id: "AD 2.22-COMFAIL", title: "Radio Communication Failure", icon: Route, category: "procedures", color: "cyan", parent: "AD 2.22" },
  
  // EBCI AD 2.23
  { id: "AD 2.23", title: "Additional Information", icon: Info, category: "general", color: "blue", subsections: [] },
];

// Color schemes
const COLOR_SCHEMES = {
  blue: { bg: "bg-blue-50 dark:bg-blue-950/30", border: "border-blue-200 dark:border-blue-800", icon: "text-blue-600 dark:text-blue-400", hover: "hover:border-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50", badge: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
  emerald: { bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200 dark:border-emerald-800", icon: "text-emerald-600 dark:text-emerald-400", hover: "hover:border-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50", badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300" },
  amber: { bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-200 dark:border-amber-800", icon: "text-amber-600 dark:text-amber-400", hover: "hover:border-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50", badge: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  gold: { bg: "bg-yellow-50 dark:bg-yellow-950/30", border: "border-yellow-300 dark:border-yellow-700", icon: "text-[#d4af37]", hover: "hover:border-[#d4af37] hover:bg-yellow-100 dark:hover:bg-yellow-900/50", badge: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300" },
  purple: { bg: "bg-purple-50 dark:bg-purple-950/30", border: "border-purple-200 dark:border-purple-800", icon: "text-purple-600 dark:text-purple-400", hover: "hover:border-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50", badge: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300" },
  teal: { bg: "bg-teal-50 dark:bg-teal-950/30", border: "border-teal-200 dark:border-teal-800", icon: "text-teal-600 dark:text-teal-400", hover: "hover:border-teal-400 hover:bg-teal-100 dark:hover:bg-teal-900/50", badge: "bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300" },
  orange: { bg: "bg-orange-50 dark:bg-orange-950/30", border: "border-orange-200 dark:border-orange-800", icon: "text-orange-600 dark:text-orange-400", hover: "hover:border-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/50", badge: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300" },
  red: { bg: "bg-red-50 dark:bg-red-950/30", border: "border-red-200 dark:border-red-800", icon: "text-red-600 dark:text-red-400", hover: "hover:border-red-400 hover:bg-red-100 dark:hover:bg-red-900/50", badge: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" },
  cyan: { bg: "bg-cyan-50 dark:bg-cyan-950/30", border: "border-cyan-200 dark:border-cyan-800", icon: "text-cyan-600 dark:text-cyan-400", hover: "hover:border-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/50", badge: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300" }
};

// Complete AIP content from DOCX file
const AIP_CONTENT = {
  "AD 2.2": {
    title: "Aerodrome Geographical and Administrative Data",
    content: `1. ARP COORDINATES: 502736N 0042710E
   Site of ARP at aerodrome: 335° MAG / 205 M from TWR

2. DIRECTION AND DISTANCE FROM CITY: 4 NM North of Charleroi

3. ELEVATION / REFERENCE TEMPERATURE: 606 FT / 22°C

4. GEOID UNDULATION AT AD ELEV PSN: 151 FT

5. MAGNETIC VARIATION / ANNUAL CHANGE: 1°E (2020) / INFO not AVBL

6. NAME OF AD OPERATOR:
   Airport Authority: Service Public de Wallonie
   Airport Management: Brussels South Charleroi Airport (B.S.C.A.) SA
   
   Address:
   Airport Authority: Direction de l'aéroport de Charleroi / Brussels South
   Mr. Marissal (Director)
   Aéroport de Charleroi / Brussels South
   Rue des Frères Wright 8
   6041 Gosselies, BELGIUM
   
   TEL:
   +32 (0) 71 25 12 15 (Airport Authority)
   +32 (0) 71 25 12 12 (Airport Inspection)
   +32 (0) 71 25 12 60 (B.S.C.A. Management)
   +32 (0) 71 25 12 50 (B.S.C.A. Operations)
   
   FAX:
   +32 (0) 71 25 12 54 (Airport Authority)
   +32 (0) 71 25 12 91 (Airport Inspection)
   +32 (0) 71 25 12 02 (B.S.C.A. Management)
   +32 (0) 71 25 12 42 (B.S.C.A. Operations)
   
   AFS: EBCIYDYX
   Website: www.brussels-charleroi-airport.com

7. TYPES OF TRAFFIC PERMITTED: IFR / VFR`
  },
  "AD 2.3": {
    title: "Operational Hours",
    content: `1. AD OPERATOR: 0530-2200 (0430-2100)
   (see also EBCI AD 2.21, § 1.1)

2. CUSTOMS AND IMMIGRATION:
   Passengers: as AD Operator
   Goods: MON to FRI (HOL excl), 0700-1100 (0600-1000) and 1145-1545 (1045-1445)
   General aviation: obligation to transmit GENDEC to customs

3. HEALTH AND SANITATION: As AD Operator

4. AIS BRIEFING OFFICE: As AD Operator

5. ATS REPORTING OFFICE (ARO): NIL

6. MET BRIEFING OFFICE: H24

7. ATS: 0515-2200 (0415-2100)

8. FUELLING: 
   Jet A1 delivery during opening hours, except for commercial based airplanes
   AVGAS available from 0700 to 2130 (0600 to 2030)
   During LVP, AVGAS station is closed

9. HANDLING: As AD Operator

10. SECURITY: As AD Operator

11. DE-ICING: As AD Operator

REMARKS:
(1) Customs clearance outside these hours possible with fees and prior notice
(2) GENDEC required for flights from/to non-EU countries - send to:
    da.controle.gosselies@minfin.fed.be at least 3 hours before arrival/departure`
  },
  "AD 2.4": {
    title: "Handling Services and Facilities",
    content: `1. CARGO-HANDLING FACILITIES:
   • Conveyor belt, bag carts, trailers
   • Waste truck, water truck
   • Tractable stairs, motorized stairs
   • Forklift, bag carts tractors (electrical & motorized)
   • Tow bar (range small size to wide body size)
   • Tow bar-less tractor (B737 to A340)
   • Ground power unit (GPU), air starter unit
   • Scissor lift, bus
   
   Handling services: push-back, towing, walk around, headset assistance,
   de-/anti-icing, external cleaning, deep cleaning, bussing, loading/offloading
   
   Nearest railway siding: Charleroi (7 KM)

2. FUEL TYPES: AVGAS 100 LL, JET A1 and UL91
   Oil types: oil for turbines

3. FUELLING FACILITIES AND CAPACITY:
   AVGAS 100 LL: 1 aircraft refueller 5,000 L, 200 L/MIN + reserve 50,000 L
   JET A1: 2 ACFT refuellers 40,000 L, 1 aircraft refueller 20,000 L
   2 x 1,200 L/MIN for each aircraft refueller + reserve 6 x 100,000 L
   Self-service for AVGAS and UL91: Payment with Bancontact card
   Credit cards accepted

4. DE-ICING FACILITIES: 5 de-icers type 2 / de-icing farm all types of aircraft

5. HANGAR SPACE: 1,700 M² (not heated)
   Freight hangar: 4,000 M² (heated)

6. REPAIR FACILITIES: All repairs

7. GENERAL AVIATION:
   Handling mandatory for non-based aircraft
   24 HR prior permission required from GA OPS Office
   Email: ga@charleroi-airport.com
   
   GA OPS Office (BSCA Business Aviation):
   TEL: +32 (0) 71 25 19 34 (0600-2000 / 0500-1900)
   FAX: +32 (0) 71 25 11 29
   
   Navigation Office:
   TEL: +32 (0) 71 25 12 14 (0300-0000 / 0200-2300)
   Email: bnav@charleroi-airport.com
   
   Handling OPS office (BSCA):
   TEL: +32 (0) 71 25 12 50/51 (0330-2200 / 0230-2100)
   Email: ops@charleroi-airport.com`
  },
  "AD 2.5": {
    title: "Passenger Facilities",
    content: `1. HOTELS: Near aerodrome and in the city

2. RESTAURANTS: At aerodrome and in the city

3. TRANSPORTATION:
   • Bus/train connection to/from any station in Belgium
   • Taxis and car hire
   • Direct coach service to/from Brussels city centre

4. MEDICAL FACILITIES:
   • First aid treatment and recovery room
   • 1 ambulance
   • Hospitals in Gosselies (3 KM) and Charleroi (7 KM)

5. BANK: Self-banking at aerodrome
   Post office: In the city

6. TOURIST OFFICE: At aerodrome / Tourist office in the city

7. OTHER FACILITIES:
   • Duty free shops
   • Travel agency
   • Foreign exchange office
   • Large guarded car park next to the terminal`
  },
  "AD 2.6": {
    title: "Rescue and Fire Fighting Services",
    content: `1. AERODROME CATEGORY FOR FIRE FIGHTING:
   CAT 7 standard
   CAT 9 upon 90 MIN prior notice

2. RESCUE EQUIPMENT:
   Water supply:
   • 275 M³ under hangar S9
   • 135 M³ under the ramp M6
   • 135 M³ under intersection of service road and ramp N1
   
   RFFS Vehicles:
   • Rescue 1, 2, 4, 5: Massive attack vehicles
   • Rescue 10: Mobile command unit
   • Rescue 3: Operational command post (PC Ops)
   • C902: Vehicle for transporting people
   • Rescue 8: Off-road intervention vehicle
   • Rescue 9: Equipment transport vehicle
   • Rescue 11: Rapid intervention vehicle
   • Rescue 12: Ambulance

3. CAPABILITY FOR REMOVAL OF DISABLED AIRCRAFT:
   Disabled aircraft will be removed by B.S.C.A. s.a. on the responsibility
   of the aircraft operator or the aircraft owner`
  },
  "AD 2.7": {
    title: "Runway Surface Condition Assessment and Reporting, and Snow Plan",
    content: `1. TYPES OF CLEARING EQUIPMENT:
   • 2 truck BOSCHUNG SNOW BOOSTER P5960 with milling cutter
   • 1 sweeper-blower Schorling with snowplough
   • 1 snowplough adaptable on VOLVO dumper
   • 1 sweeper-blower Boschung with snowplough
   • 3 sweeper-blower Schmidt with snowplough
   • 1 Caterpillar wheel loader with snowplough
   • 1 truck UNIMOG with snow plough and spreader
   • 1 truck SCANIA with sprayers of de-icing liquid
   • 3 farm truck VALTRA with snowplough
   • 1 Autobren vacuum-sweeper
   • 1 farm truck Ford
   • 1 farm truck Deutz-Fahr Agrokid 220
   • 1 farm truck Kubota B1820 with brush
   • 1 Skiddometre BV11

2. CLEARANCE PRIORITIES:
   1st: RWY 06/24 and appropriate taxiways
   2nd: Apron North and aircraft stands
   3rd: Remaining part of the movement area

3. MATERIALS FOR SURFACE TREATMENT:
   KAC (potassium acetate fluids)
   NAFO (sodium formate solids)

4. INFORMATION TRANSMISSION:
   By SNOWTAM, ATIS and RCR based on RCAM
   
   Contact for snow operations:
   Airport Authority (Service Public de Wallonie)
   TEL: +32 (0) 71 25 12 12
   TEL: +32 (0) 71 25 12 15
   FAX: +32 (0) 71 25 12 91`
  },
  "AD 2.8": {
    title: "Aprons, Taxiways and Check Locations/Positions Data",
    content: `1. APRON DESIGNATION, SURFACE AND STRENGTH:

   SOUTH APRON (General Aviation - P1-P5):
   P1: CONC, PCN 30/R/C/W/T, Elevation 596 FT
   P2: CONC, PCN 58/R/C/W/T, Elevation 598 FT
   P3: CONC, PCN 50/R/C/W/U, Elevation 595 FT
   P4: CONC, PCN 72/R/C/W/T, Elevation 592 FT
   P5: CONC, PCN 72/R/C/W/T, Elevation 583 FT
   
   NORTH APRON (Commercial - P10-P15):
   P10: CONC, PCN 73/R/B/W/U, Elevation 569 FT
   P11: CONC, PCN 73/R/B/W/T, Elevation 566 FT
   P12: CONC, PCN 73/R/B/W/T, Elevation 563 FT
   P13: CONC, PCN 73/R/B/W/T, Elevation 563 FT
   P14: CONC, PCN 73/R/B/W/T, Elevation 563 FT
   P15: CONC, PCN 73/R/B/W/U, Elevation 563 FT

2. TAXIWAY DESIGNATION, WIDTH, SURFACE AND STRENGTH:
   TWY N: 23 M, ASPH, PCN 63/F/C/W/T (N1-N6), PCN 70/F/B/W/T (N6-N7)
   TWY N1, N2, N3, N4, N5, N6: 23 M, ASPH, PCN 63/F/C/W/T
   TWY M4, M5, M6: 23 M, ASPH, PCN 63/F/C/W/T
   TWY N7: 23 M, ASPH, PCN 70/F/B/W/T
   TWY M7: 23 M, ASPH, PCN 70/F/B/W/T
   TWY S: 23 M, CONC (S1-S4), ASPH (S4-S5), PCN 78/R/C/W/T
   TWY S1, S2, S3, S4: 23 M, CONC, PCN 78/R/C/W/T
   TWY T2 and T3: 20 M, CONC/ASPH

3. VOR CHECK POINTS: NIL

4. INS CHECK POINTS: At aircraft stands (see chart AD 2.EBCI-GMC.01)

5. REMARKS: TWY S5 closed`
  },
  "AD 2.8-N": {
    title: "North Apron (P10-P15) - Commercial",
    content: `COMMERCIAL APRON - NORTH SIDE

APRON P10:
• Surface: CONC
• PCN: 73/R/B/W/U
• Elevation: 569 FT
• Aircraft higher than code C allowed on West side subject to approval

APRON P11:
• Surface: CONC
• PCN: 73/R/B/W/T
• Elevation: 566 FT

APRON P12:
• Surface: CONC
• PCN: 73/R/B/W/T
• Elevation: 563 FT

APRON P13:
• Surface: CONC
• PCN: 73/R/B/W/T
• Elevation: 563 FT

APRON P14:
• Surface: CONC
• PCN: 73/R/B/W/T
• Elevation: 563 FT

APRON P15:
• Surface: CONC
• PCN: 73/R/B/W/U
• Elevation: 563 FT
• No code D aircraft

DEDICATED STANDS FOR LARGE AIRCRAFT:
Stands 51B, 55B, 57B, 67B and 80B dedicated to:
• Airbus A340-300
• A330-200
• A330-900
• Code D aircraft

APRON MOVEMENT RULES:
When RWY 24 in use for landing:
- Inbound traffic enters via TWY M4, M5 or M6
- Outbound traffic leaves via TWY M5, M6 or M7
- Aircraft pushed facing east, clockwise movement

When RWY 06 in use for landing:
- Inbound traffic enters via TWY M5, M6 or M7
- Outbound traffic leaves via TWY M4, M5 or M6
- Aircraft pushed facing west, counter-clockwise movement

Outbound traffic:
- Aircraft wingspan below 36 M: pushed on taxilane F
- A340-300, A330-200, A330-900: pushed on taxilane G
- 180° turns on Apron South are FORBIDDEN`
  },
  "AD 2.8-S": {
    title: "South Apron (P1-P5) - General Aviation",
    content: `GENERAL AVIATION APRON - SOUTH SIDE

APRON P1:
• Surface: CONC
• PCN: 30/R/C/W/T
• Elevation: 596 FT
• Restrictions: Code A or B aircraft only
  Height 4.78 M MAX, Length 18 M MAX, Wingspan 16.61 M MAX

APRON P2:
• Surface: CONC
• PCN: 58/R/C/W/T
• Elevation: 598 FT
• Restrictions: Code A or B aircraft only
  Height 4.78 M MAX, Length 18 M MAX, Wingspan 16.61 M MAX

APRON P3:
• Surface: CONC
• PCN: 50/R/C/W/U
• Elevation: 595 FT
• Restrictions: Aircraft with wingspan and length 20 M MAX only

APRON P4:
• Surface: CONC
• PCN: 72/R/C/W/T
• Elevation: 592 FT
• Stand Restrictions:
  - Stands 19, 20, 21, 23, 24, 26, 27: Wingspan 13.60 M MAX
  - Stands 22, 25, 28: Wingspan 12 M MAX
  - Long duration parking of tail wheel aircraft prohibited on stands 26, 27, 28

APRON P5:
• Surface: CONC
• PCN: 72/R/C/W/T
• Elevation: 583 FT
• Restrictions: Medium body aircraft MAX (tail wheel aircraft prohibited)
  - Stand 30: Wingspan over 21 M and length over 22 M not available
  - Stands 31, 32, 33, 34: Wingspan over 32 M not available
  - Stand 36: Not available

IMPORTANT RESTRICTIONS:
Aircraft with engine(s) running cannot stay more than 5 minutes on parking
positions P1, P2, P3, P4. Pre-departure checks shall be performed on
dedicated areas after receiving taxi clearance.`
  },
  "AD 2.9": {
    title: "Surface Movement Guidance and Control System and Markings",
    content: `1. AIRCRAFT STAND IDENTIFICATION SIGNS:
   On apron P10, P11, P12, P13, P14 and P15
   
   Taxiway guide lines: Guidance sign boards at entrance of taxiways
   to runways and at intersection of taxiways
   
   APRON N TAXILANE MARKINGS:
   • Taxilane F: Centreline markings orange, lights green/yellow (LED)
   • Taxilane G: Centreline markings yellow, lights green (LED)
   • Taxilane H: Centreline markings blue, lights green/blue (LED)
   
   Visual docking/parking guidance system: Marshalling on apron North
   Follow-me: available on request on apron North and South

2. RUNWAY MARKINGS AND LIGHTING:
   RWY markings: Designation, threshold, touchdown zone, centre line
   and side stripe markings
   Aiming point of RWY 06 placed at 300 M from the threshold
   RWY lighting: see EBCI AD 2.14
   
   TWY markings: Centre line, edge lines, holding positions at
   TWY/RWY intersections and intermediate holding position
   TWY lighting: see EBCI AD 2.15

3. STOP BARS: At holding positions
   Runway guard lights: Elevated runway guard lights at all CAT 1 stop bars

4. OTHER RUNWAY PROTECTION MEASURES: NIL`
  },
  "AD 2.10": {
    title: "Aerodrome Obstacles",
    content: `CLOSE-IN OBSTACLES:

RWY 06 CLOSE-IN:
ID        | Position              | ALT (FT) | Lighted
EBCI_642  | 502827.05N 0042907.56E | 716      | No
EBCI_272  | 502830.15N 0042929.20E | 716      | No

RWY 24 CLOSE-IN:
ID        | Position              | ALT (FT) | Lighted
EBCI_023  | 502713.53N 0042608.97E | 653      | Yes
EBCI_571  | 502713.94N 0042603.58E | 644      | Yes
EBCI_022  | 502711.97N 0042602.38E | 650      | Yes
EBCI_648  | 502719.86N 0042557.16E | 644      | Yes
EBCI_070  | 502718.28N 0042551.39E | 653      | Yes
EBCI_021  | 502704.76N 0042556.33E | 676      | No
EBCI_028  | 502712.82N 0042553.64E | 650      | Yes
EBCI_019  | 502703.18N 0042532.71E | 735      | No
EBCI_756  | 502713.67N 0042553.30E | 644      | Yes
EBCI_759  | 502714.40N 0042551.43E | 644      | Yes
EBCI_027  | 502712.59N 0042558.35E | 630      | Yes
EBCI_072  | 502712.08N 0042544.36E | 660      | Yes
EBCI_001  | 502659.45N 0042539.12E | 683      | No

VISUAL SEGMENT SURFACE (VSS) PENETRATION:
Vegetation obstacles affecting:
• LNAV RWY 06
• LNAV/VNAV RWY 06
• VOR RWY 06

Note: Details on EBCI aerodrome obstacles on chart AD2 EBCI AOC.01`
  },
  "AD 2.11": {
    title: "Meteorological Information Provided",
    content: `1. ASSOCIATED MET OFFICE: EBCI MET

2. HOURS OF SERVICE: H24

3. OFFICE RESPONSIBLE FOR TAF: EBBR
   Periods of validity: 30 HR
   Interval of issuance: 6 HR

4. TREND FORECAST: Available
   Interval of issuance: 30 MIN

5. BRIEFING/CONSULTATION PROVIDED: Personal consultation, TEL

6. FLIGHT DOCUMENTATION: Charts, abbreviated plain language text
   Languages used: English

7. CHARTS AVAILABLE FOR BRIEFING:
   • Surface charts
   • Altitude charts
   • Prognostic altitude charts
   • Prognostic chart of significant weather
   • Tropopause and maximum wind chart

8. SUPPLEMENTARY EQUIPMENT:
   • Self-briefing terminal
   • FAX
   • Real-time weather display

9. ATS UNITS PROVIDED WITH INFORMATION:
   • Charleroi TWR
   • Charleroi APP

10. CONTACT INFORMATION:
    International aviation: TEL: +32 (0) 71 25 12 24
    FAX: +32 (0) 2 206 28 29 (EBBR)
    VFR flights, gliding, ballooning: TEL: 0902 / 88 173 (CONSULTEL)

Note: Communications automatically recorded on tape`
  },
  "AD 2.12": {
    title: "Runway Physical Characteristics",
    content: `RUNWAY 06:
• True Bearing: 065.47°
• Dimensions: 2905 x 45 M
• Strength: PCN 64/F/A/W/T
• Surface: ASPH
• THR Coordinates: 502724.66N 0042632.97E
• THR Elevation: 604.0 FT
• TDZ Elevation: 604.0 FT
• THR Geoid Undulation: 151.0 FT
• RWY End Coordinates: 502759.51N 0042832.95E
• Slope: +0.1% (1155 M), -0.8% (1250 M), 0.0% (500 M)
• CWY Dimensions: 260 x 150 M
• Strip Dimensions: 3175 x 300 M
• RESA Dimensions: 200 x 90 M
• Arresting System: AVBL O/R at 460 M from end of RWY

RUNWAY 24:
• True Bearing: 245.47°
• Dimensions: 3055 x 45 M
• Strength: PCN 64/F/A/W/T
• Surface: ASPH
• THR Coordinates: 502752.83N 0042809.95E
• THR Elevation: 576.1 FT
• TDZ Elevation: 581.0 FT
• THR Geoid Undulation: 151.0 FT
• RWY End Coordinates: 502720.59N 0042618.93E
• Slope: 0.0% (500 M), +0.8% (1250 M), -0.1% (1155 M)
• CWY Dimensions: 205 x 150 M
• Strip Dimensions: 3175 x 300 M
• RESA Dimensions: 240 x 90 M
• Arresting System: AVBL O/R at 205 M from end of RWY

Note: Slope changes do not give pilot unobstructed line of sight over
half the RWY length at all points 3 M above the RWY`
  },
  "AD 2.13": {
    title: "Declared Distances",
    content: `RUNWAY 06:
• TORA: 2905 M
• TODA: 3165 M
• ASDA: 2905 M
• LDA: 2600 M

RUNWAY 24:
• TORA: 3055 M
• TODA: 3260 M
• ASDA: 3055 M
• LDA: 2405 M

INTERSECTION DEPARTURES:
(Authorized when A-SMGCS available, or RVR >= 550 M)

RWY 06:
From N2: TORA 2800 M
From N3: TORA 2160 M
From S2: TORA 2345 M
From S3: TORA 1670 M

RWY 24:
From N4: TORA 1820 M
From N5: TORA 2405 M
From N6: TORA 2837 M
From S3: TORA 1257 M
From S4: TORA 1820 M`
  },
  "AD 2.14": {
    title: "Approach and Runway Lighting",
    content: `RUNWAY 06:
• Approach Lighting: SALS, 420 M, LIH
• VASIS: PAPI (left / 3°), MEHT 69 FT
• Threshold Lights: Green
• Touchdown Zone Lights: NIL
• Runway End Lights: Red
• Stopway Lights: NIL
• Runway Centre Line Lights: 2905 M, 15 M spacing, LIH
  - White: 0 to 2005 M
  - Red/White: 2005 to 2605 M
  - Red: 2605 to 2905 M
• Runway Edge Lights: 2905 M, 30 M spacing, LIH
  - Red: 0 to 305 M
  - White: 305 to 2305 M
  - Yellow: 2305 to 2905 M
• LED: Approach lighting, threshold, end and edge lights

RUNWAY 24:
• Approach Lighting: PALS CAT II/III, 900 M, LIH
• VASIS: PAPI (left / 3°), MEHT 59 FT
• Threshold Lights: Green
• Touchdown Zone Lights: 900 M
• Runway End Lights: Red
• Stopway Lights: NIL
• Runway Centre Line Lights: 3055 M, 15 M spacing, LIH
  - White: 0 to 2155 M
  - Red/White: 2155 to 2755 M
  - Red: 2755 to 3055 M
• Runway Edge Lights: 3055 M, 30 M spacing, LIH
  - Red: 0 to 650 M
  - White: 650 to 2455 M
  - Yellow: 2455 to 3055 M
• LED: TDZ, approach lighting, threshold, end and edge lights`
  },
  "AD 2.15": {
    title: "Other Lighting and Secondary Power Supply",
    content: `1. ABN/IBN: NIL

2. LDI LOCATION AND LIGHTING: Next to T1 (lighted)
   WDI LOCATION AND LIGHTING:
   • Next to LDI (lighted)
   • East of P5 (lighted)
   • At 305 M from THR RWY 24 on southeast side (lighted)

3. TAXIWAY EDGE LIGHTING:
   TWY N (N1-N6) and M4-M6: Curves only (LED)
   TWY N (N1-N6) straight line: Reflectors
   TWY N (N6-N7) and M6-M7: Edge lighting (LED)
   TWY S (S1-S4): Curves only (LED)
   
   TAXIWAY CENTRE LINE LIGHTING:
   TWY N, N1, N2, N3, N4, N5, N6, N7, M4, M5, M6, M7 (LED)
   TWY S, S1, S2, S3, S4 (LED)

4. SECONDARY POWER SUPPLY:
   Available to all lighting at aerodrome
   Switch-over time:
   • 15 SEC for approach lighting and PAPI 06
   • 0 SEC for all other lights`
  },
  "AD 2.16": {
    title: "Helicopter Landing Area",
    content: `Helicopter take-off and final approach shall be performed on RWY 06/24.

No dedicated helicopter landing area is available.
Helicopters use the main runway for operations.`
  },
  "AD 2.17": {
    title: "ATS Airspace",
    content: `1. DESIGNATION: Charleroi CTR

2. LATERAL LIMITS:
   503339N 0043136E then clockwise arc radius 5.5 NM centered on
   502817N 0043335E - 502255N 0043533E - 502010N 0041725E then
   clockwise arc radius 5.5 NM centered on 502532N 0041525E -
   503054N 0041324E - 503339N 0043136E

3. VERTICAL LIMITS: SFC to 2500 FT AMSL

4. AIRSPACE CLASSIFICATION: Class D

5. ATS UNIT CALL SIGN: Charleroi Tower
   Language(s): English

6. TRANSITION ALTITUDE: 4500 FT AMSL

7. HOURS OF ACTIVATION: As ATS operational hours
   OPR HR may vary - check with Brussels FIC

8. REMARKS:
   UAS can be encountered in UAS geographical zones EBCI VLL0, VLL1 and VLL2
   (see ENR 5.1, § 4). Systematic tracking of UAS by ATC cannot be ensured.`
  },
  "AD 2.18": {
    title: "ATS Communication Facilities",
    content: `APPROACH/RADAR (APP/TAR):
Call Sign: Charleroi Approach
• Primary: 133.130 MHz (8.33 kHz CH)
• Supplementary: 128.730 MHz (8.33 kHz CH)
• Military: 257.800 MHz, 372.100 MHz
• Emergency: 121.500 MHz, 243.000 MHz

TOWER (TWR):
Call Sign: Charleroi Tower
• Primary: 121.305 MHz (8.33 kHz CH)
• Military: 257.800 MHz
• Emergency: 121.500 MHz, 243.000 MHz

GROUND (GND):
Call Sign: Charleroi Ground
• Frequency: 121.805 MHz (8.33 kHz CH)

ATIS:
Call Sign: Charleroi Information
• Voice: 134.630 MHz (H24)
• Via VOR: 115.700 MHz (H24)
• D-ATIS: Available

VDF (Charleroi Homer):
Available on frequencies:
121.305, 121.805, 133.130, 128.725,
257.800, 372.100, 121.500, 243.000 MHz`
  },
  "AD 2.19": {
    title: "Radio Navigation and Landing Aids",
    content: `DVOR/DME (GOS):
• Frequency: 115.700 MHz
• Channel: CH 104X
• Hours: H24
• Position: 502800.7N 0042852.6E
• DME Antenna Elevation: 600 FT
• Coverage: 30 NM / FL 260
• Magnetic Variation: 1°E (2020)

ILS RUNWAY 24 (CAT III):
LOC (IGC):
• Frequency: 110.900 MHz
• Hours: H24
• Position: 502716.3N 0042604.3E
• Course: 245° GEO / 1.47 NM from THR 24
• No back beam available
• LOC only reliable within 35° either side of course line

GP:
• Frequency: 330.800 MHz
• Hours: H24
• Position: 502745.5N 0042759.5E
• Slope: 3°
• RDH: 54 FT

DME (IGC):
• Channel: CH 46X
• Hours: H24
• Position: 502745.7N 0042759.5E (collocated with GP)
• Elevation: 577 FT

MARKERS:
OM: 75 MHz (dash/dash), H24
• Position: 502922N 0043319E
• 3.62 NM from THR 24 (or use IGC DME fix)

MM: 75 MHz (dot/dash), H24
• Position: 502808N 0042905E
• 0.64 NM from THR 24 (or use IGC DME fix)`
  },
  "AD 2.20": {
    title: "Local Aerodrome Regulations",
    content: `This section contains local regulations for EBCI airport operations.
Click on subsections below for detailed information:

• General - Overall restrictions and requirements
• Taxi Regulations - Taxiway usage rules
• Apron Regulations - Apron operations and procedures
• Runway Regulations - Preferential runway system
• Specific Traffic Regulations - Prohibited activities and training flights`
  },
  "AD 2.20-GEN": {
    title: "General",
    content: `1. GENERAL REGULATIONS

1.1 RESTRICTIONS FOR AIRCRAFT CATEGORIES:
• Non-based VFR flights PROHIBITED unless able to maintain 160 KT on
  approach if requested (except helicopters)
• All arriving IFR flights by CAT A and B aircraft are STRICTLY PPR
  Permission required from EBCI ATC by phone prior departure
• Only jet and turbine engine aircraft allowed 2100-2200 (2000-2100)
• Arrivals after 2100: aircraft must maintain 160 KT till OM,
  IAF overflown by 2130 at latest

1.2 ALTERNATE AERODROME:
EBCI shall NOT be used as alternate unless prior agreement with
BSCA operations (TEL: +32 (0) 71 25 12 50)

1.3 GROUND SURVEILLANCE - MODE S TRANSPONDERS:
• EBCI equipped with advanced ground surveillance using Mode S
• Transponders must operate when aircraft on ground
• Select XPDR (or equivalent), AUTO if available - NOT OFF or STBY
• Set assigned Mode A code from request for push-back or taxi
• When parked: Set Mode A code 2000 before selecting OFF or STBY
• ACAS shall not be selected before receiving clearance to line up
• Aircraft taxiing without flight plan: Select Mode A code 2000

1.4 PRE-DEPARTURE CHECKS:
Aircraft with engine(s) running cannot stay more than 5 MIN on parking
positions P1, P2, P3 and P4. Pre-departure checks shall be performed on
dedicated areas:
• RWY 24 in use: on P5 or dedicated zone on TWY S in front of stands 35-39
• RWY 06 in use: at holding point S1 or S2`
  },
  "AD 2.20-TAXI": {
    title: "Taxi Regulations",
    content: `2. TAXI REGULATIONS

2.1 TAXIWAY RESTRICTIONS:
• Circular turn path RWY 24: radius 30 M, steering angle max 45°
• TWY S2 and S3: prohibited for wingspan > 36 M
• TWY S4: prohibited for wingspan > 20 M if turning right toward THR 24
• 180° backtrack on RWY 24: only on turn pad after RWY end (aircraft > 20 T)
• Max 2 aircraft with wingspan >= 24 M simultaneously on RWY 06/24 and TWY S
• Aircraft wingspan >= 36 M on RWY: no aircraft allowed on TWY S (and vice versa)
• A340-300 on TWY N: outer engines on idle (FOD prevention)
• A340-300, A330-200, A330-900 on TWY S: only via S1 and S4 with prior approval,
  follow-me car and wingman required
• TWY T2 and T3: restricted to military use`
  },
  "AD 2.20-APRON": {
    title: "Apron Regulations",
    content: `3. APRON REGULATIONS

3.1 GENERAL:
• Engine start outside official opening hours PROHIBITED
• Cross bleed procedure: 1st engine on stand, 2nd on taxilane after push
• Taxilanes F, G, H: ATS guidance only - pilots/drivers responsible for separation

3.2 APRON NORTH (P1-P5):
• Pre-departure checks limited to 5 minutes max
• Engine running aircraft must proceed to designated check areas

3.3 APRON SOUTH (P10-P15):
When RWY 24 in use for landing:
- Inbound traffic enters via TWY M4, M5 or M6
- Outbound traffic leaves via TWY M5, M6 or M7
- Aircraft pushed facing east, clockwise movement

When RWY 06 in use for landing:
- Inbound traffic enters via TWY M5, M6 or M7
- Outbound traffic leaves via TWY M4, M5 or M6
- Aircraft pushed facing west, counter-clockwise movement`
  },
  "AD 2.20-RWY": {
    title: "Runway Regulations",
    content: `4. RUNWAY REGULATIONS

4.1 PREFERENTIAL RUNWAY SYSTEM:
RWY 24 assigned for take-off and landing when:
• Runway dry
• Cross wind <= 10 KT
• Tailwind <= 8 KT
• When RWYCC < 5: tailwind max 5 KT

4.2 RUNWAY CROSSING:
• All runway crossings require explicit ATC clearance
• Read back of crossing clearance mandatory
• Hold short instructions must be acknowledged

4.3 INTERSECTION DEPARTURES:
Authorized when A-SMGCS available, or RVR >= 550 M
Available intersections:
• RWY 06: N2, N3, S2, S3
• RWY 24: N4, N5, N6, S3, S4`
  },
  "AD 2.20-SPEC": {
    title: "Specific Traffic Regulations",
    content: `5. SPECIFIC TRAFFIC REGULATIONS

PROHIBITED:
• Aircraft without radio
• Glider flights
• ULM flights
• Balloon flights
• Parachuting overhead aerodrome
• Acrobatic flights within traffic circuit

TRAINING AND TEST FLIGHTS:
• Only by based aircraft/operators with wingspan < 36 M
• Propeller aircraft <= 6T MTOW: 0600-2100 (0500-2000)
• Prior permission from Charleroi TWR required
• During LVP: all training flights PROHIBITED

MILITARY OPERATIONS:
• TWY T2 and T3 restricted to military use
• Military coordination required for certain operations`
  },
  "AD 2.21": {
    title: "Noise Abatement Procedures",
    content: `This section contains noise abatement procedures for EBCI.
Click on subsections below for detailed information:

• General - Noise certification and restrictions
• Ground Procedures - Engine test run limitations
• Arrival Procedures - Continuous descent and approach requirements
• Departure Procedures - Climb gradient and power settings`
  },
  "AD 2.21-GEN": {
    title: "General",
    content: `1. GENERAL NOISE REGULATIONS

1.1 NOISE CERTIFICATION:
Aircraft must be noise certificated according to ICAO Annex 16, Volume I

RESTRICTIONS 0530-0600 AND 2100-2200:
Aircraft with MTOW > 8618 KG certified to chapters 2, 3, 4 or 5:
restricted to max QC of 5 per movement

QC FORMULA: QC = 10^[(B-85)/10]
Where B equals:
• Take-off: half sum of certified fly-over and sideline noise levels (EPNdB) at MTOW
• Landing: certified approach noise level (EPNdB) at MLW minus 9 EPNdB

AT 2200 (2100) AERODROME CLOSED except:
Delayed landing of based aircraft (scheduled before 2200) provided:
• Delay due to circumstances beyond operator's control
• Operator's points "P" do not exceed 0.616 per day (yearly average)`
  },
  "AD 2.21-GND": {
    title: "Ground Procedures",
    content: `2. GROUND PROCEDURES

2.1 ENGINE TEST RUNS:
• High power tests: PROHIBITED on aircraft stands
• Outdoor high power tests: PROHIBITED 2000-0800 (1900-0700)
• Idle checks: only during opening hours
• Full power tests performed on P5, only when RWY 24 in use

2.2 APU USAGE:
• APU usage should be minimized when GPU available
• Extended APU operations require coordination

2.3 TAXI PROCEDURES:
• Use minimum power settings during taxi
• Avoid unnecessary engine run-ups near noise-sensitive areas`
  },
  "AD 2.21-ARR": {
    title: "Arrival Procedures",
    content: `3. ARRIVAL PROCEDURES

3.1 NOISE ABATEMENT APPROACH:
Use continuous descent and reduced power/reduced drag when:
• ILS available, runway clear and dry
• Visibility > 1900 M, ceiling > 500 FT above AD ELEV
• Cross wind < 15 KT, tailwind < 5 KT (incl. gusts)
• No adverse weather conditions

3.2 CONTINUOUS DESCENT OPERATIONS (CDO):
Facilitated at ATC discretion based on radar vectoring or RNP approach

3.3 APPROACH SPEEDS:
• Maintain approach speed as briefed
• Speed reductions coordinated with ATC
• Late configuration changes avoided when possible`
  },
  "AD 2.21-DEP": {
    title: "Departure Procedures",
    content: `4. DEPARTURE PROCEDURES

4.1 CLIMB GRADIENT:
Maintain NET climb gradient 4% MIN until passing 3000 FT QNH

4.2 NOISE ABATEMENT TAKE-OFF:
From take-off to 2100 FT QNH:
• Take-off power, take-off flaps, climb speed V2 + 10 KT MIN

At 2100 FT QNH:
• Maintain take-off flaps, climb speed V2 + 10 to 20 KT
• Adjust power per noise abatement thrust schedule

From 2100 to 3600 FT QNH:
• Start accelerating, start retracting flaps
• Maintain positive rate of climb

At 3600 FT QNH:
• Accelerate to en-route climb speed

4.3 DEPARTURE TRACKS:
• Follow published SID tracks precisely
• Early turns avoided over noise-sensitive areas`
  },
  "AD 2.22": {
    title: "Flight Procedures",
    content: `This section contains flight procedures for EBCI.
Click on subsections below for detailed information:

• IFR Flights (Inbound) - Arrivals, STARs, approaches
• IFR Flights (Outbound) - Departures, SIDs, climb requirements
• Low Visibility Procedures - CAT II/III operations
• VFR Flights - Visual reporting points, circuits
• Radio Communication Failure - Emergency procedures`
  },
  "AD 2.22-IFR-IN": {
    title: "IFR Flights (Inbound)",
    content: `2. IFR FLIGHTS (INBOUND)

2.1 GENERAL:
When radar service available, military aircraft not VOR or RNAV capable
may only expect radar vectors to ILS or LOC RWY 24

2.2 HOLDING PATTERNS:
GOSLY Fix: GOS DVOR/DME
Turn: Right
Inbound track: 245° MAG
Minimum level: 3000 FT QNH
Max speed: 230 KIAS

2.3 APPROACH PROCEDURES:
RNP RWY 06: ABLIX - REKPI - CI06F - RW06 - CI06T
RNP RWY 24: GUGNO - VAMKA - CI24F - RW24 - CI24T
RWY 24 RNAV1 to ILS available

2.4 STANDARD INSTRUMENT ARRIVALS (STAR):
ARVOL6A, CIV6A, KOK6A, NIK6A, BATTY6A, BATTY7B, LNO6A, LNO7B
(Details on chart AD 2.EBCI-STAR.01)

2.5 VISUAL APPROACHES:
IFR traffic MTOW > 11 T: do not intercept final approach closer than
6 NM from THR (except emergency)`
  },
  "AD 2.22-IFR-OUT": {
    title: "IFR Flights (Outbound)",
    content: `3. IFR FLIGHTS (OUTBOUND)

3.1 STANDARD INSTRUMENT DEPARTURES (SID):
RWY 06: SOPOK9X, RITAX8X, CIV6X, LNO8X, SPI8X
RWY 24: SOPOK5U, SOPOK1Y, RITAX5U, RITAX9Y, MEDIL5Y, CIV5Y,
        LNO5U, LNO9Y, SPI5U, SPI9Y
(Details on EBCI SID charts)

3.2 CLIMB REQUIREMENTS:
All traffic initially climb to 4000 FT QNH unless instructed otherwise
Additional requirements for traffic via SOPOK-ETENO-ROPUV and RITAX

3.3 DEPARTURE CLEARANCE:
• Clearance obtained before engine start
• Read-back required for all clearance elements
• Squawk code set before taxi`
  },
  "AD 2.22-LVP": {
    title: "Low Visibility Procedures",
    content: `4. LOW VISIBILITY PROCEDURES

4.1 FACILITIES:
• RWY 24: Approved for CAT II and III
• CAT II: RVR 550 M to 300 M
• CAT III: RVR 300 M to 150 M
• Take-off: RVR 150 M MIN

4.2 LVP CRITERIA:
Preparation: Visibility < 1500 M and/or ceiling <= 300 FT
Operations: RVR < 550 M and/or ceiling < 200 FT
Termination: Visibility >= 800 M and ceiling >= 200 FT

4.3 LVP RESTRICTIONS:
• AVGAS station closed during LVP
• All training flights PROHIBITED
• Vehicle movements restricted
• Follow-me mandatory for certain operations`
  },
  "AD 2.22-VFR": {
    title: "VFR Flights",
    content: `5. VFR FLIGHTS

5.1 VISUAL REPORTING POINTS:
NW: Railway station Obaix-Buzet (503210N 0042148E)
N: Frasnes-les-Gosselies gas tank (503241N 0042718E)
NE: Village of Tilly (503338N 0043315E)
NA: Water tower east of Gosselies (502840N 0042701E)
SW: Belfry of Thuin (502023N 0041712E)
S: Bultia intersection N5/N574 (502036N 0042824E)
SE: Village of Presles (502302N 0043443E)
SA: CORA shopping at R3 exit (502437N 0042940E)
E: Spy gas station on E42 (502941N 0044206E)

5.2 VISUAL CIRCUIT:
Aircraft up to 6 T: 1500 FT AMSL
Aircraft > 6 T below 2500 FT: left turn (RWY 06), right turn (RWY 24)

5.3 VFR TRAFFIC RESTRICTIONS:
• Non-based VFR flights PROHIBITED unless able to maintain 160 KT
• Prior permission required from ATC`
  },
  "AD 2.22-COMFAIL": {
    title: "Radio Communication Failure",
    content: `6. RADIO COMMUNICATION FAILURE

IFR: Leave Charleroi CTR on track 248° MAG at 2500 FT QNH MAX

VFR in circuit: Make full-stop landing

Other VFR: Leave via NW (north of RWY) or S (south of RWY)

TRANSPONDER CODES:
• Set transponder to 7600 (communication failure)
• Squawk IDENT if able

LIGHT SIGNALS:
Be prepared to receive and comply with light gun signals from TWR:
• Steady Green: Cleared to land / Cleared for takeoff
• Flashing Green: Return for landing / Cleared to taxi
• Steady Red: Give way and continue circling / Stop
• Flashing Red: Airport unsafe / Taxi clear of runway
• Flashing White: Return to starting point
• Alternating Red/Green: Exercise extreme caution`
  },
  "AD 2.23": {
    title: "Additional Information",
    content: `ATIS BROADCAST:
Serving inbound and outbound traffic H24

ATIS MESSAGE ELEMENTS (in order):
1. Aerodrome name: CHARLEROI
2. Alphabetical designator: INFO (A to Z)
3. Time: HHMM
4. Type of approach to expect
5. Runway in use for ARR and DEP
6. RSCD time (if applicable)
7. Runway surface condition
8. RWYCC (Runway Condition Code)
9. Transition level
10. Operational status
11. Surface wind (direction, speed, variations)
12. Visibility (CAVOK or value)
13. RVR (TDZ, MID, END)
14. Present weather
15. Cloud base or vertical visibility
16. Air temperature
17. Dewpoint temperature
18. Altimeter setting (QNH)
19. Recent weather
20. Supplementary meteorological phenomena
21. Landing forecast (TREND)
22. Confirmation request

IMPORTANT:
When rapidly changing weather conditions exist, weather data may be
omitted with phrase: "MET REPORT OMITTED DUE TO RAPID CHANGES"
Request omitted data from ATC.

PILOT REQUIREMENTS:
• Listen to ATIS before first contact with ATS
• Acknowledge receipt: "INFORMATION [designator] RECEIVED"
• ATS will confirm validity of designator`
  }
};

export default function AIPInfoPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [aipData, setAipData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSection, setSelectedSection] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [quickRefSettingsOpen, setQuickRefSettingsOpen] = useState(false);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [quickRefSections, setQuickRefSections] = useState(DEFAULT_QUICK_REF_SECTIONS);
  const [savingSettings, setSavingSettings] = useState(false);
  const [selectedQuickRef, setSelectedQuickRef] = useState(null);
  const [quickRefDialogOpen, setQuickRefDialogOpen] = useState(false);

  const isMasterAdmin = user?.role === "master_admin";

  useEffect(() => {
    loadAIPData();
    loadHiddenSections();
    loadQuickRefSections();
  }, []);

  const loadAIPData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/aip/data");
      setAipData(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        await refreshAIPData();
      } else {
        toast.error("Failed to load AIP data");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadHiddenSections = async () => {
    try {
      const response = await api.get("/aip/hidden-sections");
      setHiddenSections(response.data.hidden_sections || []);
    } catch (error) {
      setHiddenSections([]);
    }
  };

  const loadQuickRefSections = async () => {
    try {
      const response = await api.get("/aip/quick-reference");
      setQuickRefSections(response.data.sections || DEFAULT_QUICK_REF_SECTIONS);
    } catch (error) {
      setQuickRefSections(DEFAULT_QUICK_REF_SECTIONS);
    }
  };

  const refreshAIPData = async () => {
    setRefreshing(true);
    try {
      const response = await api.post("/aip/refresh");
      setAipData(response.data);
      toast.success("AIP data refreshed");
    } catch (error) {
      toast.error("Failed to refresh AIP data");
    } finally {
      setRefreshing(false);
    }
  };

  const handleSectionClick = (section) => {
    setSelectedSection(section);
    setDialogOpen(true);
  };

  const handleQuickRefClick = (cardId) => {
    setSelectedQuickRef(QUICK_REF_CARDS[cardId]);
    setQuickRefDialogOpen(true);
  };

  const toggleSectionVisibility = (sectionId) => {
    setHiddenSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const toggleQuickRefSection = (sectionId) => {
    setQuickRefSections(prev => 
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const saveHiddenSections = async () => {
    setSavingSettings(true);
    try {
      await api.put("/aip/hidden-sections", { hidden_sections: hiddenSections });
      toast.success("Card visibility settings saved");
      setSettingsOpen(false);
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const saveQuickRefSections = async () => {
    setSavingSettings(true);
    try {
      await api.put("/aip/quick-reference", { sections: quickRefSections });
      toast.success("Quick Reference settings saved");
      setQuickRefSettingsOpen(false);
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const getSectionContent = (sectionId) => {
    return AIP_CONTENT[sectionId] || { 
      title: AIP_SECTIONS.find(s => s.id === sectionId)?.title || sectionId,
      content: "Content not available. Please refer to official AIP documentation." 
    };
  };

  // Get main sections (no parent) that are visible
  const mainSections = AIP_SECTIONS.filter(s => !s.parent && !hiddenSections.includes(s.id));
  
  // Filter by search
  const filteredSections = mainSections.filter(section => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const content = AIP_CONTENT[section.id];
    const hasMatchingSubsection = AIP_SECTIONS.filter(s => s.parent === section.id).some(sub => 
      sub.title.toLowerCase().includes(query) || AIP_CONTENT[sub.id]?.content?.toLowerCase().includes(query)
    );
    return (
      section.title.toLowerCase().includes(query) ||
      section.id.toLowerCase().includes(query) ||
      content?.content?.toLowerCase().includes(query) ||
      hasMatchingSubsection
    );
  });

  // Group sections by category
  const groupedSections = {
    general: filteredSections.filter(s => s.category === "general"),
    apron: filteredSections.filter(s => s.category === "apron" || s.category === "ga" || s.category === "north"),
    runway: filteredSections.filter(s => s.category === "runway"),
    ats: filteredSections.filter(s => s.category === "ats"),
    safety: filteredSections.filter(s => s.category === "safety" || s.category === "obstacles"),
    regulations: filteredSections.filter(s => s.category === "regulations"),
    procedures: filteredSections.filter(s => s.category === "procedures"),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Loader2 className="h-12 w-12 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="aip-info-page">
      {/* Header */}
      <div className="sticky-page-header">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a] dark:text-white font-['Outfit']">AIP Information</h1>
          <p className="text-[#64748b] dark:text-gray-400 mt-1">EBCI - Charleroi / Brussels South</p>
        </div>
        <div className="flex items-center gap-3">
          {isMasterAdmin && (
            <Button variant="outline" onClick={() => setSettingsOpen(true)} className="border-[#e2e8f0] dark:border-[#334155]">
              <Settings2 size={16} className="mr-2" />
              Manage Cards
            </Button>
          )}
          <Button onClick={refreshAIPData} disabled={refreshing} variant="outline" className="border-[#e2e8f0] dark:border-[#334155]">
            <RefreshCw size={16} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <a href="https://ops.skeyes.be/html/belgocontrol_static/eaip/eAIP_Main/html/eAIP/EB-AD-2.EBCI-en-GB.html" target="_blank" rel="noopener noreferrer" className="text-sm text-[#d4af37] hover:text-[#b5952f] flex items-center gap-1">
            <ExternalLink size={14} /> Official AIP
          </a>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b]" size={18} />
        <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search AIP sections..." className="pl-10" />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-6 p-3 bg-[#f8fafc] dark:bg-[#1e293b] rounded-lg border border-[#e2e8f0] dark:border-[#334155]">
        <span className="text-xs font-medium text-[#64748b] mr-2">Legend:</span>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-blue-500"></div><span className="text-xs text-[#64748b]">General Info</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-emerald-500"></div><span className="text-xs text-[#64748b]">South Apron (P1-P5)</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-amber-500"></div><span className="text-xs text-[#64748b]">North Apron (P10-P15)</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-purple-500"></div><span className="text-xs text-[#64748b]">Runway</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-teal-500"></div><span className="text-xs text-[#64748b]">ATS/Nav</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-500"></div><span className="text-xs text-[#64748b]">Safety/Obstacles</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-orange-500"></div><span className="text-xs text-[#64748b]">Regulations</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-cyan-500"></div><span className="text-xs text-[#64748b]">Procedures</span></div>
      </div>

      {/* Quick Reference */}
      <Card className="mb-6 border-[#e2e8f0] dark:border-[#334155]">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg font-['Outfit']"><Info size={18} className="text-[#d4af37]" />Quick Reference</CardTitle>
            {isMasterAdmin && (
              <Button variant="ghost" size="sm" onClick={() => setQuickRefSettingsOpen(true)} className="text-[#64748b] hover:text-[#0f172a]">
                <Plus size={16} className="mr-1" /> Customize
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Basic Info Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
            <div className="p-3 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg"><p className="text-xs text-[#64748b] mb-1">Elevation</p><p className="font-bold">606 FT</p></div>
            <div className="p-3 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg"><p className="text-xs text-[#64748b] mb-1">Runway</p><p className="font-bold">06/24</p></div>
            <div className="p-3 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg"><p className="text-xs text-[#64748b] mb-1">TWR</p><p className="font-mono font-bold">121.305</p></div>
            <div className="p-3 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg"><p className="text-xs text-[#64748b] mb-1">GND</p><p className="font-mono font-bold">121.805</p></div>
            <div className="p-3 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg"><p className="text-xs text-[#64748b] mb-1">Trans Alt</p><p className="font-bold">4,500 FT</p></div>
            <div className="p-3 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg"><p className="text-xs text-[#64748b] mb-1">Fire Cat</p><p className="font-bold">7-9</p></div>
          </div>
          
          {/* Dynamic Quick Reference Cards */}
          {quickRefSections.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#e2e8f0] dark:border-[#334155]">
              {quickRefSections.map(cardId => {
                const card = QUICK_REF_CARDS[cardId];
                if (!card) return null;
                const colors = COLOR_SCHEMES[card.color] || COLOR_SCHEMES.blue;
                const Icon = card.icon || FileText;
                return (
                  <div 
                    key={cardId}
                    onClick={() => handleQuickRefClick(cardId)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${colors.bg} ${colors.border} ${colors.hover}`}
                    data-testid={`quick-ref-${cardId}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.icon} bg-white/50 dark:bg-black/20`}>
                        <Icon size={20} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{card.title}</p>
                        <p className="text-xs text-[#64748b]">Click to view details</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section Groups */}
      <div className="space-y-6">
        {groupedSections.general.length > 0 && (
          <SectionGroup title="GENERAL INFORMATION" icon={Info} color="blue" sections={groupedSections.general} onSectionClick={handleSectionClick} hiddenSections={hiddenSections} />
        )}
        {groupedSections.apron.length > 0 && (
          <SectionGroup title="APRONS & TAXIWAYS" icon={Navigation} color="gold" sections={groupedSections.apron} onSectionClick={handleSectionClick} hiddenSections={hiddenSections} />
        )}
        {groupedSections.safety.length > 0 && (
          <SectionGroup title="SAFETY & OBSTACLES" icon={Shield} color="red" sections={groupedSections.safety} onSectionClick={handleSectionClick} hiddenSections={hiddenSections} />
        )}
        {groupedSections.runway.length > 0 && (
          <SectionGroup title="RUNWAY INFORMATION" icon={Plane} color="purple" sections={groupedSections.runway} onSectionClick={handleSectionClick} hiddenSections={hiddenSections} />
        )}
        {groupedSections.ats.length > 0 && (
          <SectionGroup title="ATS & NAVIGATION" icon={Radio} color="teal" sections={groupedSections.ats} onSectionClick={handleSectionClick} hiddenSections={hiddenSections} />
        )}
        {groupedSections.regulations.length > 0 && (
          <SectionGroup title="LOCAL REGULATIONS" icon={FileText} color="orange" sections={groupedSections.regulations} onSectionClick={handleSectionClick} hiddenSections={hiddenSections} />
        )}
        {groupedSections.procedures.length > 0 && (
          <SectionGroup title="FLIGHT PROCEDURES" icon={Route} color="cyan" sections={groupedSections.procedures} onSectionClick={handleSectionClick} hiddenSections={hiddenSections} />
        )}
      </div>

      {/* Section Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 font-['Outfit']">
              {selectedSection && (
                <>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${COLOR_SCHEMES[selectedSection.color]?.bg} ${COLOR_SCHEMES[selectedSection.color]?.icon}`}>
                    {selectedSection.icon && <selectedSection.icon size={20} />}
                  </div>
                  <div>
                    <span className="text-xs text-[#64748b] font-normal block">EBCI {selectedSection.id}</span>
                    {getSectionContent(selectedSection.id).title}
                  </div>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-4">
            {selectedSection && (
              <>
                {/* Main content */}
                <div className="mt-4 p-4 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg border border-[#e2e8f0] dark:border-[#334155]">
                  <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed">{getSectionContent(selectedSection.id).content}</pre>
                </div>
                
                {/* Subsections if any */}
                {(() => {
                  const subsections = AIP_SECTIONS.filter(s => s.parent === selectedSection.id && !hiddenSections.includes(s.id));
                  return subsections.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold mb-3 text-[#64748b]">Subsections:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {subsections.map(subsection => (
                          <button
                            key={subsection.id}
                            onClick={() => {
                              setSelectedSection(subsection);
                            }}
                            className={`p-3 rounded-lg border text-left transition-all ${COLOR_SCHEMES[subsection.color]?.bg} ${COLOR_SCHEMES[subsection.color]?.border} ${COLOR_SCHEMES[subsection.color]?.hover}`}
                          >
                            <div className="flex items-center gap-2">
                              <ChevronRight size={14} className={COLOR_SCHEMES[subsection.color]?.icon} />
                              <span className="text-sm font-medium">{subsection.title}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Back to parent link if this is a subsection */}
                {selectedSection.parent && (
                  <button
                    onClick={() => {
                      const parentSection = AIP_SECTIONS.find(s => s.id === selectedSection.parent);
                      if (parentSection) setSelectedSection(parentSection);
                    }}
                    className="mt-4 text-sm text-[#d4af37] hover:underline flex items-center gap-1"
                  >
                    Back to {selectedSection.parent}
                  </button>
                )}
              </>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Master Admin Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Manage AIP Card Visibility</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-2">
              {AIP_SECTIONS.map(section => (
                <div key={section.id} className={`flex items-center justify-between p-3 bg-[#f8fafc] dark:bg-[#1e293b] rounded-lg ${section.parent ? 'ml-6' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded flex items-center justify-center ${COLOR_SCHEMES[section.color]?.bg} ${COLOR_SCHEMES[section.color]?.icon}`}>
                      <section.icon size={16} />
                    </div>
                    <div>
                      <span className="text-xs text-[#64748b]">{section.id}</span>
                      <p className="text-sm font-medium">{section.title}</p>
                    </div>
                  </div>
                  <Switch checked={!hiddenSections.includes(section.id)} onCheckedChange={() => toggleSectionVisibility(section.id)} />
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveHiddenSections} disabled={savingSettings} className="bg-[#0f172a] text-white">
              {savingSettings ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Reference Settings Dialog - Master Admin Only */}
      <Dialog open={quickRefSettingsOpen} onOpenChange={setQuickRefSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Outfit']">
              <Bookmark size={18} className="text-[#d4af37]" />
              Customize Quick Reference
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#64748b] mb-4">Select which sections to display in the Quick Reference panel:</p>
          <div className="space-y-3">
            {Object.values(QUICK_REF_CARDS).map(card => {
              const colors = COLOR_SCHEMES[card.color] || COLOR_SCHEMES.blue;
              const Icon = card.icon || FileText;
              return (
                <div key={card.id} className={`flex items-center justify-between p-3 rounded-lg border ${colors.bg} ${colors.border}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded flex items-center justify-center ${colors.icon} bg-white/50 dark:bg-black/20`}>
                      <Icon size={16} />
                    </div>
                    <span className="text-sm font-medium">{card.title}</span>
                  </div>
                  <Checkbox 
                    checked={quickRefSections.includes(card.id)} 
                    onCheckedChange={() => toggleQuickRefSection(card.id)}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
            <Button variant="outline" onClick={() => setQuickRefSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveQuickRefSections} disabled={savingSettings} className="bg-[#0f172a] text-white">
              {savingSettings ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Reference Detail Dialog */}
      <Dialog open={quickRefDialogOpen} onOpenChange={setQuickRefDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 font-['Outfit']">
              {selectedQuickRef && (
                <>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${COLOR_SCHEMES[selectedQuickRef.color]?.bg} ${COLOR_SCHEMES[selectedQuickRef.color]?.icon}`}>
                    {selectedQuickRef.icon && <selectedQuickRef.icon size={20} />}
                  </div>
                  <span>{selectedQuickRef.title}</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-4">
            {selectedQuickRef && (
              <div className="mt-4 p-4 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg border border-[#e2e8f0] dark:border-[#334155]">
                <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed">{selectedQuickRef.content}</pre>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Section Group Component
function SectionGroup({ title, icon: Icon, color, sections, onSectionClick, hiddenSections }) {
  const colorClass = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber: "text-amber-600 dark:text-amber-400",
    blue: "text-blue-600 dark:text-blue-400",
    purple: "text-purple-600 dark:text-purple-400",
    teal: "text-teal-600 dark:text-teal-400",
    orange: "text-orange-600 dark:text-orange-400",
    cyan: "text-cyan-600 dark:text-cyan-400",
    red: "text-red-600 dark:text-red-400",
    gold: "text-[#d4af37]"
  }[color] || "text-blue-600";

  return (
    <div>
      <h3 className={`text-sm font-semibold ${colorClass} mb-3 flex items-center gap-2`}>
        <Icon size={16} /> {title}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {sections.map(section => (
          <SectionCard key={section.id} section={section} onClick={() => onSectionClick(section)} hasSubsections={AIP_SECTIONS.some(s => s.parent === section.id && !hiddenSections.includes(s.id))} />
        ))}
      </div>
    </div>
  );
}

// Section Card Component
function SectionCard({ section, onClick, hasSubsections }) {
  const colors = COLOR_SCHEMES[section.color] || COLOR_SCHEMES.blue;
  const Icon = section.icon || FileText;
  
  return (
    <Card className={`cursor-pointer transition-all duration-200 ${colors.bg} ${colors.border} ${colors.hover} border`} onClick={onClick} data-testid={`aip-section-${section.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colors.icon} bg-white/50 dark:bg-black/20`}>
            <Icon size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${colors.badge}`}>{section.id}</span>
              {hasSubsections && <span className="text-[10px] text-[#64748b]">+{AIP_SECTIONS.filter(s => s.parent === section.id).length} subsections</span>}
            </div>
            <p className="text-sm font-medium mt-1 line-clamp-2">{section.title}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
